import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ApiError } from '../api/client'
import {
  linkIncidents,
  problemRecordQuery,
  removeProblem,
  unlinkIncident,
  updateProblem,
  type ProblemWriteResult,
} from '../api/problems'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden } from '../ui/Forbidden'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * `Problems::view` - fisa unei probleme, la /app/problems/view/{id}.
 *
 * Trei lucruri, in ordinea in care le cauta cineva:
 *   1. cum se ocoleste ACUM;
 *   2. care e cauza;
 *   3. ce incidente au dovedit-o.
 *
 * Ocolirea sta prima si nu ultima. Cauza e ce vrea sa stie cel care repara;
 * ocolirea e ce vrea sa stie cel care e sunat la 3 dimineata, si el e mai
 * grabit.
 */

const STYLES = ['css/adm.css']

export function ProblemRecordPage({ id }: { id: number }) {
  const shell = useQuery(shellQuery(moduleOf('/problems')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const page = useQuery(problemRecordQuery(id))
  const query = useQueryClient()
  const navigate = useNavigate()

  const [form, setForm] = useState({ title: '', status: '1', workaround: '', root_cause: '', description: '' })
  const [tickets, setTickets] = useState('')
  const [busy, setBusy] = useState(false)

  /* Formularul porneste de la ce e in baza, si se reasaza cand raspunsul se
     schimba (dupa o salvare, dupa o legatura noua). */
  const loaded = page.data?.problem
  useEffect(() => {
    if (!loaded) return
    setForm({
      title: loaded.title,
      status: String(loaded.status),
      workaround: loaded.workaround,
      root_cause: loaded.rootCause,
      description: loaded.description,
    })
  }, [loaded])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error instanceof ApiError && page.error.status === 404) {
    return (
      <Content>
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <h4>No such problem</h4>
          <p style={{ marginBottom: 0 }}>It was deleted, or it belongs to another customer.</p>
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />

  const data = page.data
  const problem = data.problem
  const reload = () => void query.invalidateQueries({ queryKey: ['problems'] })

  const done = (result: ProblemWriteResult, message: string): boolean => {
    if (result.status !== 'OK') {
      toast(result.message ?? 'It did not work.', 'Problems', 'error')
      return false
    }
    toast(message, 'Problems')
    return true
  }

  const failed = (error: unknown) =>
    toast(error instanceof ApiError ? error.message : 'It did not work.', 'Problems', 'error')

  const save = (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void updateProblem(id, form)
      .then((result) => {
        setBusy(false)
        if (done(result, 'Saved')) reload()
      })
      .catch((error: unknown) => {
        setBusy(false)
        failed(error)
      })
  }

  const link = (event: React.FormEvent) => {
    event.preventDefault()
    if (tickets.trim() === '') return
    void linkIncidents(id, tickets)
      .then((result) => {
        if (result.status !== 'OK') {
          toast(result.message ?? 'It did not work.', 'Problems', 'error')
          return
        }
        /*
         * Se spune CATE au intrat din cate s-au cerut.
         *
         * Un numar gresit, sau al altui client, nu intra - iar „am legat 2 din
         * 4" e singurul loc in care omul afla ca doua n-au intrat.
         */
        if (result.linked === result.asked) {
          toast(`Linked ${result.linked ?? 0}.`, 'Incidents')
        } else {
          toast(
            `Linked ${result.linked ?? 0} of ${result.asked ?? 0}. The rest do not exist, belong to another customer, or were already linked.`,
            'Incidents',
            'warning',
          )
        }
        setTickets('')
        reload()
      })
      .catch(failed)
  }

  const unlink = (ticket: number) => {
    void unlinkIncident(id, ticket)
      .then((result) => {
        if (done(result, 'Unlinked')) reload()
      })
      .catch(failed)
  }

  const drop = () => {
    if (!window.confirm('This deletes the problem. The incidents stay untouched.')) return
    void removeProblem(id)
      .then((result) => {
        if (done(result, 'Deleted')) void navigate({ to: '/problems' })
      })
      .catch(failed)
  }

  return (
    <Content>
      <div id="content">
        <div className="row">
          <div className="col-md-8">
            <div className="adm-card" style={{ marginBottom: 16 }}>
              <span className="adm-pill" style={{ background: problem.statusColour, color: '#fff' }}>
                {problem.statusLabel}
              </span>
              {problem.ciName !== '' ? (
                <span style={{ marginLeft: 10 }}>
                  on{' '}
                  <a href={`/app/cmdb/view/${problem.ciId}`} target="_blank" rel="noopener">
                    {problem.ciName}
                  </a>
                </span>
              ) : (
                problem.ciId > 0 && (
                  <span className="adm-pill adm-pill-bad" style={{ marginLeft: 10 }}>
                    deleted item #{problem.ciId}
                  </span>
                )
              )}
              <span className="text-muted small" style={{ marginLeft: 10 }}>
                opened {problem.created}
                {problem.resolved !== '' && <> &middot; resolved {problem.resolved}</>}
              </span>
            </div>

            {/*
              Ocolirea, ca panou aparte si colorat. Ingropata intr-un formular cu
              cinci campuri, ar fi fost citita de nimeni. Aici e primul lucru pe
              care il vede cineva care a ajuns pe pagina asta dintr-un incident,
              in mijlocul noptii.
            */}
            {problem.workaround !== '' && (
              <div className="adm-card" style={{ borderLeft: '3px solid #c2571f', marginBottom: 16 }}>
                <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
                  <i className="fas fa-life-ring"></i> Until it is fixed
                </p>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{problem.workaround}</p>
              </div>
            )}

            <form id="problem-form" onSubmit={save}>
              <div className="form-group">
                <label className="control-label">Title</label>
                <input
                  type="text"
                  className="form-control input-sm"
                  maxLength={255}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="control-label">Status</label>
                <select
                  className="form-control input-sm"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {data.statuses.map((pair) => (
                    <option key={pair.value} value={pair.value}>
                      {pair.label}
                    </option>
                  ))}
                </select>
                <span className="help-block small">
                  {/*
                    Se explica treapta de mijloc, fiindca e cea care se sare.
                    „Eroare cunoscuta" nu inseamna „aproape rezolvata".
                  */}
                  <strong>Known error</strong> means the cause is understood and the workaround is known, but the
                  permanent fix has not been made. A problem can sit in that state for months, and that is fine.
                </span>
              </div>

              <div className="form-group">
                <label className="control-label">How to work around it, until it is fixed</label>
                <textarea
                  className="form-control input-sm"
                  rows={3}
                  value={form.workaround}
                  onChange={(e) => setForm({ ...form, workaround: e.target.value })}
                />
                <span className="help-block small">Shown on every incident linked to this problem.</span>
              </div>

              <div className="form-group">
                <label className="control-label">Root cause</label>
                <textarea
                  className="form-control input-sm"
                  rows={4}
                  value={form.root_cause}
                  onChange={(e) => setForm({ ...form, root_cause: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="control-label">What is known</label>
                <textarea
                  className="form-control input-sm"
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="form-group">
                <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
                  Save
                </button>
                <button type="button" className="btn btn-link btn-sm remove-problem" onClick={drop}>
                  Delete problem
                </button>
              </div>
            </form>
          </div>

          <div className="col-md-4">
            <div className="adm-card">
              <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
                Incidents <span className="badge">{data.incidents.length}</span>
              </p>

              <form id="link-incidents-form" onSubmit={link} style={{ marginBottom: 12 }}>
                <div className="input-group input-group-sm">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ticket numbers: 1001, 1002"
                    value={tickets}
                    onChange={(e) => setTickets(e.target.value)}
                  />
                  <span className="input-group-btn">
                    <button className="btn btn-default" type="submit">
                      Link
                    </button>
                  </span>
                </div>
              </form>

              {data.incidents.length === 0 ? (
                <p className="text-muted small" style={{ margin: 0 }}>
                  {/* O problema fara incidente e o afirmatie fara nimic in
                      spate. Se spune, ca sa nu ramana asa. */}
                  No problem stands up on its own. Link the incidents that proved it &mdash; otherwise it stays an
                  opinion.
                </p>
              ) : (
                <table className="table table-condensed" style={{ margin: 0 }}>
                  <tbody>
                    {data.incidents.map((row) => (
                      <tr key={row.id}>
                        <td style={{ width: 70 }}>
                          <a href={`/app/easeedesk/ticket/${row.id}`} target="_blank" rel="noopener">
                            #{row.id}
                          </a>
                        </td>
                        <td>
                          {row.subject}
                          <div className="text-muted small">
                            {row.created.slice(0, 10)}
                            {!row.closed && (
                              <>
                                {' '}
                                &middot; <span className="adm-pill adm-pill-warn">open</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td style={{ width: 24 }}>
                          <button
                            type="button"
                            className="btn btn-link btn-xs unlink-incident text-muted"
                            title="Unlink"
                            onClick={() => unlink(row.id)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </Content>
  )
}
